import { DOCUMENT, NgClass } from '@angular/common';
import { Component, effect, forwardRef, inject, OnInit, Renderer2, signal, viewChild } from '@angular/core';
import { DroolsBuildResponse, DroolsBuildResponseMessage, FileDef } from '@compiere-ws/models/apiz-domains';
import { DroolsService } from '@iupics-components/specific/window/drools-editor-ui/drools.service';
import ButtonUiComponent from '@iupics-components/standard/fields/button-ui/button-ui.component';
import InputSwitchUiComponent from '@iupics-components/standard/fields/input-switch-ui/input-switch-ui.component';
import InputTextUiComponent from '@iupics-components/standard/fields/input-text-ui/input-text-ui.component';
import ModalUiComponent from '@iupics-components/standard/layouts/modal-ui/modal-ui.component';
import {
  EditorContentChangeEvent,
  EditorDestroyEvent,
  EditorReadyEvent,
  EditorSaveEvent,
  MonacoEditorComponent,
} from '@iupics-components/standard/monaco-editor/monaco-editor.component';
import { MonacoEditorService } from '@iupics-components/standard/monaco-editor/monaco-editor.service';
import { MessageManagerService } from '@iupics-manager/managers/message/message-manager.service';
import { Global } from '@iupics-manager/models/global-var';
import { IupicsMessage } from '@iupics-manager/models/iupics-message';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ContextMenuService } from '@web-desktop/components/workspace/controllers/context-menu/context-menu.service';
import { MenuItem, TreeNode } from 'primeng/api';
import { ProgressBarModule } from 'primeng/progressbar';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { SidebarModule } from 'primeng/sidebar';
import {
  Tree,
  TreeModule,
  TreeNodeContextMenuSelectEvent,
  TreeNodeExpandEvent,
  TreeNodeSelectEvent,
} from 'primeng/tree';
import { firstValueFrom, map, Observable, of, tap, throwError } from 'rxjs';
import { InfoDialogType } from '../info-dialog/info-dialog.component';
import SpecificWindowUiComponent from '../specific-window-ui/specific-window-ui.component';

function isFalsy(data: unknown): boolean {
  if (data === undefined || data === null) {
    return true;
  }
  if (Array.isArray(data) && data.length === 0) {
    return true;
  }
  if (typeof data === 'string' && data.trim().length === 0) {
    return true;
  }
  if (typeof data === 'number' && data === 0) {
    return true;
  }
  if (typeof data === 'boolean' && data === false) {
    return true;
  }
  if (typeof data === 'object' && Object.keys(data).length === 0) {
    return true;
  }
  return false;
}

@Component({
  selector: 'iu-drools-editor-ui',
  standalone: true,
  imports: [
    ProgressBarModule,
    ScrollPanelModule,
    NgClass,
    forwardRef(() => ButtonUiComponent),
    SidebarModule,
    forwardRef(() => ModalUiComponent),
    TranslateModule,
    MonacoEditorComponent,
    TreeModule,
    InputSwitchUiComponent,
    ButtonUiComponent,
    InputTextUiComponent,
  ],
  templateUrl: './drools-editor-ui.component.html',
  styleUrl: './drools-editor-ui.component.scss',
})
export class DroolsEditorUiComponent extends SpecificWindowUiComponent implements OnInit {
  // DI services
  readonly #droolsService = inject(DroolsService);
  readonly #cmService = inject(ContextMenuService);
  readonly #document = inject(DOCUMENT);
  readonly #renderer = inject(Renderer2);
  readonly #translator = inject(TranslateService);

  // editor service
  #editorService: MonacoEditorService;
  filename = signal<string>('');

  //#region tree
  files: TreeNode[] = [];
  tree = viewChild<Tree>('tree');
  inputCmp = viewChild<InputTextUiComponent>('input');
  private sortFn = (a: TreeNode, b: TreeNode): number => {
    if (!a.leaf && b.leaf) {
      return -1;
    }
    if (a.leaf && !b.leaf) {
      return 1;
    }
    return a.label.localeCompare(b.label);
  };
  //#endregion

  private mapper = map<FileDef[], TreeNode[]>((fileDefs) => fileDefs.map(this.fileDefToTreeNode.bind(this)));
  private finder = (treeNode: TreeNode) => (tn: TreeNode) => tn.label === treeNode.label && tn.leaf === treeNode.leaf;
  private fileDefToTreeNode = (fileDef: FileDef): TreeNode =>
    ({
      label: fileDef.path,
      leaf: !fileDef.isDirectory,
      expandedIcon: 'fa fa-folder-open',
      collapsedIcon: this.#getIcon(fileDef.isDirectory),
      data: { edited: signal(false) },
    }) as TreeNode;
  private initFiles$: Observable<TreeNode[]> = this.#droolsService.getRules().pipe(this.mapper);
  loading = false;

  selection: TreeNode[] = [];
  private selectionForCM: TreeNode[] = [];
  private selectionForAction: { action: 'copy' | 'cut'; selection: TreeNode[] };
  private selectedNode: TreeNode;

  //#region build
  buildOnSave = false;
  building = false;
  //#endregion

  //#region context-menu
  cmTreeItems: () => MenuItem[] = () => [
    {
      label: 'new folder',
      title: 'new folder',
      id: 'new_folder',
      visible: !(this.selectionForCM?.length > 1),
      command: () => this.#newItem('new_folder'),
    },
    {
      label: 'new file',
      title: 'new file',
      id: 'new_file',
      visible: !(this.selectionForCM?.length > 1),
      command: () => this.#newItem('new_file'),
    },
    {
      label: 'copy',
      title: 'copy',
      id: 'copy',
      visible: !isFalsy(this.selection),
      command: () => this.#preparePasteFor('copy'),
    },
    {
      label: 'cut',
      title: 'cut',
      id: 'cut',
      visible: !isFalsy(this.selection),
      command: () => this.#preparePasteFor('cut'),
    },
    {
      label: 'paste',
      title: 'paste',
      id: 'paste',
      visible: !isFalsy(this.selectionForAction),
      command: this.paste.bind(this),
    },
    {
      label: 'delete',
      title: 'delete',
      id: 'delete',
      visible: !isFalsy(this.selectionForCM),
      command: this.delete.bind(this),
    },
  ];
  fakeCM = {
    show: () => {},
  };
  selectedFileForCm: TreeNode;
  newTreeNodeLocation: TreeNode;
  inputBlurListener: () => void;
  //#endregion

  constructor() {
    super();
    effect(() => {
      const inputCmp = this.inputCmp();
      if (inputCmp !== undefined) {
        this.inputBlurListener?.();
        this.inputBlurListener = this.#renderer.listen(
          inputCmp.inputRef.nativeElement,
          'blur',
          this.onInputEnd.bind(this)
        );
        inputCmp.inputRef.nativeElement.focus();
      }
    });
    effect(() => {
      const tree = this.tree();
      // override onNodeClick to trigger popup before selection is handle
      const orig_onNodeClick = tree.onNodeClick.bind(tree);
      tree['orig_onNodeClick'] = orig_onNodeClick;
      const new_onNodeClick = (event: Event, node: TreeNode): void => {
        if (node.leaf) {
          // throwError is used to forcefully go into error section
          (this.#editorService.isEdited()
            ? this.#showPopup(
                this.#translator.instant('droolsEditor.dialog.changeSummary'),
                this.#translator.instant('droolsEditor.dialog.changeDetail')
              )
            : throwError(undefined)
          ).subscribe({
            next: () => {
              // save
              const content = this.#editorService.getCurrentContent();
              this.#save(content).subscribe({
                next: () => {
                  tree['orig_onNodeClick'](event, node);
                },
              });
            },
            error: () => {
              // no save
              this.selectedNode?.data.edited.set(false);
              tree['orig_onNodeClick'](event, node);
            },
          });
        } else {
          tree['orig_onNodeClick'](event, node);
        }
      };
      tree.onNodeClick = new_onNodeClick.bind(this);
    });
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.loadFiles();
  }

  loadFiles(): void {
    this.loading = true;
    this.initFiles$.pipe(tap(() => (this.loading = false))).subscribe((files) => (this.files = files));
  }

  /** used for new item */
  onInputEnd(e: Event): void {
    this.inputBlurListener?.();
    const { value } = e.target as HTMLInputElement;
    const idx = this.newTreeNodeLocation.children.findIndex((tn) => tn.key === 'input_new');
    const treeNode = this.newTreeNodeLocation.children.splice(idx, 1)[0];
    if (!value) {
      return;
    }

    treeNode.label = value;
    const path = this.#getPath(treeNode);
    this.#droolsService
      .newFile({ destination: path, type: treeNode.data.replace('new_', '') })
      .subscribe((fileDefs) => {
        for (const fileDef of fileDefs) {
          const newTreeNode = this.fileDefToTreeNode(fileDef);
          this.newTreeNodeLocation.children.push(newTreeNode);
        }
        this.newTreeNodeLocation.children.sort(this.sortFn);
      });
  }

  onEditorReady(e: EditorReadyEvent): void {
    this.#editorService = e.service;
  }

  onEditorDestroy(e: EditorDestroyEvent): void {
    this.#editorService = undefined;
  }

  onEditorSave(e: EditorSaveEvent): void {
    this.#save(e.value).subscribe();
  }

  #save(content: string, buildOnSave: boolean = this.buildOnSave): Observable<DroolsBuildResponse> {
    this.building = buildOnSave;
    return this.#droolsService.saveRule(this.filename(), content, buildOnSave).pipe(
      tap(() => {
        if (buildOnSave) {
          this.building = false;
        }
      }),
      tap((response) => {
        if (response.messages?.length) {
          for (const message of response.messages) {
            this.#newErrorMessage(message);
          }
        } else {
          this.#editorService.setInitialContent(content);
          this.selectedNode.data.edited.set(this.#editorService.isEdited());
        }
      })
    );
  }

  #newErrorMessage(message: DroolsBuildResponseMessage): void {
    MessageManagerService.newMessageStatic(
      new IupicsMessage(`${message.path}:${message.line}:${message.column}`, message.msg)
    );
  }

  onEditorContentChange(_: EditorContentChangeEvent): void {
    this.selectedNode.data.edited.set(this.#editorService.isEdited());
  }

  handleNodeSelection(e: TreeNodeSelectEvent): void {
    if (this.selection.length !== 1) {
      return;
    }
    if (e.node.leaf) {
      const path = this.#getPath(e.node);
      const currentPath = this.filename();
      if (path === currentPath) {
        return;
      }
      this.selectedNode = e.node;
      this.#droolsService.getRule(path).subscribe((content) => {
        this.filename.set(path);
        this.#editorService.setContent(content);
      });
    } else {
      e.node.expanded = !e.node.expanded;
      this.tree().onNodeExpand.emit(e);
    }
  }

  /** @returns an observable which trigger next on confirm and error on cancel. */
  #showPopup(summary: string, detail: string): Observable<any> {
    return new Observable<void>((sub) => {
      Global.infoDialog.message = { summary, detail };
      Global.infoDialog.dialogType = InfoDialogType.CONFIRM_YESNO;
      Global.infoDialog.showInfoDialog();
      const confirm = Global.infoDialog.confirm.subscribe(() => {
        confirm.unsubscribe();
        cancel.unsubscribe();
        sub.next();
        sub.complete();
      });
      const cancel = Global.infoDialog.cancel.subscribe(() => {
        confirm.unsubscribe();
        cancel.unsubscribe();
        sub.error();
        sub.complete();
      });
    });
  }

  onNodeExpand(e: TreeNodeExpandEvent): void {
    this.#loadNextNodes(e.node);
  }

  async #loadNextNodes(node: TreeNode): Promise<void> {
    if (!node.children) {
      node.loading = true;
      try {
        const files = await firstValueFrom(this.#droolsService.getRules(this.#getPath(node)).pipe(this.mapper));
        node.children = files;
      } catch (ignored) {}
      node.loading = false;
    }
  }

  #getPath(node: MenuItem | TreeNode): string {
    let _node = node;
    let path = node.label;
    while (_node.parent) {
      path = _node.parent.label + '/' + path;
      _node = _node.parent;
    }
    return '/' + path;
  }

  #getIcon(isDirectory: boolean): string {
    return isDirectory ? 'fa fa-folder' : 'fa fa-file';
  }

  build(): void {
    (this.#editorService.isEdited()
      ? this.#save(this.#editorService.getCurrentContent(), false)
      : of(undefined)
    ).subscribe(() => {
      this.building = true;
      this.#droolsService
        .build()
        .pipe(tap(() => (this.building = false)))
        .subscribe((response) => {
          if (response.messages?.length) {
            for (const message of response.messages) {
              this.#newErrorMessage(message);
            }
          } else {
            MessageManagerService.newMessageStatic(new IupicsMessage('Build', 'Successfull', 'success'));
          }
        });
    });
  }

  onTreeNodeContextMenuSelectEvent(e: TreeNodeContextMenuSelectEvent): void {
    if (e.node.leaf && e.node.parent) {
      this.newTreeNodeLocation = e.node.parent;
    } else if (!e.node.leaf) {
      this.newTreeNodeLocation = e.node;
    } else {
      this.newTreeNodeLocation = { children: this.files };
    }

    this.selectionForCM = [...this.selection];
    this.#cmService.emitter.emit({ menuItems: this.cmTreeItems(), originalEvent: e.originalEvent as MouseEvent });
  }

  onContextMenu(e: MouseEvent): void {
    this.newTreeNodeLocation = { children: this.files, label: '' };
    const treeNode = this.getParentElement(e.target as HTMLElement, 'P-TREENODE');
    if (treeNode === this.#document.documentElement) {
      this.selectionForCM = [...this.selection];
      this.#cmService.emitter.emit({ menuItems: this.cmTreeItems(), originalEvent: e });
    }
  }

  getParentElement(el: HTMLElement, stopEl: HTMLElement | string = this.#document.documentElement): HTMLElement {
    if (
      el.parentElement &&
      !(typeof stopEl === 'string' ? el.parentElement.tagName === stopEl : el.parentElement === stopEl)
    ) {
      return this.getParentElement(el.parentElement, stopEl);
    }

    return el.parentElement ?? el;
  }

  #newItem(type: 'new_file' | 'new_folder'): void {
    this.#loadNextNodes(this.newTreeNodeLocation).then(() => {
      this.newTreeNodeLocation.expanded = true;
      this.tree().onNodeExpand.emit({
        node: this.newTreeNodeLocation,
        originalEvent: this.#document.createEvent('MouseEvent'),
      });
      this.newTreeNodeLocation.children.push({
        key: 'input_new',
        data: type,
        type: 'input',
        selectable: false,
        icon: this.#getIcon(type === 'new_folder'),
      });
    });
  }

  #preparePasteFor(action: 'copy' | 'cut'): void {
    this.selectionForAction = { action, selection: [...this.selectionForCM] };
  }

  paste(): void {
    const { action, selection } = this.selectionForAction;
    const destination = this.#getPath(this.newTreeNodeLocation);
    const sources = selection.map(this.#getPath);
    const obs: Observable<FileDef[]> = this.#droolsService[action === 'copy' ? 'copyRules' : 'moveRules']({
      sources,
      destination,
    });

    obs.subscribe({
      next: (fileDefs) => {
        if (action === 'cut') {
          // remove all cut node from actual tree
          for (const selected of selection) {
            let arr = this.files;
            if (selected.parent) {
              arr = selected.parent.children;
            }
            const idx = arr.findIndex(this.finder(selected));
            if (idx !== -1) {
              arr.splice(idx, 1);
            }
          }
        }

        for (const fileDef of fileDefs) {
          const treeNode = this.fileDefToTreeNode(fileDef);
          const idx = this.newTreeNodeLocation.children.findIndex(this.finder(treeNode)); // don't need for new file or folder
          if (idx === -1) {
            this.newTreeNodeLocation.children.push(treeNode);
          }
        }
        this.newTreeNodeLocation.children.sort(this.sortFn);
      },
      complete: () => {
        this.selectionForAction = undefined;
        this.selectionForCM = [];
        this.selection = [];
      },
    });
  }

  delete(): void {
    const getFilesForDeleteMsg = (files: TreeNode<any>[]) =>
      (files.length > 2 ? [files[0], files[1], files[2]] : files)
        .map(this.#getPath)
        .join(', ')
        .concat(files.length > 3 ? ', ...' : '');
    this.#showPopup(
      this.#translator.instant('droolsEditor.dialog.deleteSummary'),
      this.#translator.instant('droolsEditor.dialog.deleteDetail', { files: getFilesForDeleteMsg(this.selectionForCM) })
    ).subscribe({
      next: () => {
        this.#droolsService.deleteRules({ sources: this.selectionForCM.map(this.#getPath) }).subscribe({
          next: (fileDefs) => {
            for (const selected of this.selectionForCM) {
              let arr = this.files;
              if (selected.parent) {
                arr = selected.parent.children;
              }
              const idx = arr.findIndex(this.finder(selected));
              const fdIdx = fileDefs.findIndex((fd) => fd.path === selected.label && fd.isDirectory === !selected.leaf);
              if (fdIdx !== -1 && idx !== -1) {
                arr.splice(idx, 1, this.fileDefToTreeNode(fileDefs[fdIdx]));
              } else if (idx !== -1) {
                arr.splice(idx, 1);
              }
            }
          },
          complete: () => {
            this.selectionForCM = [];
            this.selection = [];
          },
        });
      },
      error: () => {}, // empty to avoid error-interceptor to trigger
    });
  }
}

const Aria = {
  selected: 'aria-selected',
  posinset: 'aria-posinset',
  level: 'aria-level',
};
export type Aria = (typeof Aria)[keyof typeof Aria];
